import {
    Close as CloseIcon,
    Code as CodeIcon,
    Description as DescriptionIcon,
    Settings as SettingsIcon,
    Timeline as TimelineIcon,
    Build as ToolIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
// import { aiManagerApi, ToolDetail } from '../../../apis/ai-manager';

// Temporary local implementation until import issues are resolved
const aiManagerApi = {
  getToolDetail: async (toolId: number) => {
    // This will be replaced with actual API call
    return {
      toolId,
      toolName: "Loading...",
      description: "Loading tool details...",
      category: 'utility',
      isActive: true,
      timeoutSeconds: 30,
      executionCount: 0,
      successRate: 0,
      avgExecutionTime: 0,
      lastExecutedAt: new Date().toISOString(),
      schema: {
        properties: {},
        required: [],
        type: 'object'
      },
      dependencies: [],
      executionHistory: []
    };
  }
};

interface Tool {
  toolId: number;
  toolName: string;
  description: string;
  category: string;
  isActive: boolean;
  timeoutSeconds: number;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutedAt: string;
  comId?: number;
}

interface ToolDetailModalProps {
  open: boolean;
  tool: Tool | null;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tool-detail-tabpanel-${index}`}
      aria-labelledby={`tool-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ToolDetailModal: React.FC<ToolDetailModalProps> = ({ open, tool, onClose }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentToolData, setCurrentToolData] = useState<Tool | null>(null);
  const [toolSchema, setToolSchema] = useState<any>(null);
  const [dependencies, setDependencies] = useState<Array<{
    name: string;
    version: string;
    type: 'external' | 'internal';
  }>>([]);
  const [executionHistory, setExecutionHistory] = useState<any[]>([]);

  useEffect(() => {
    if (open && tool) {
      loadToolDetail();
    }
  }, [open, tool]);

  const loadToolDetail = async () => {
    if (!tool) return;

    setLoading(true);
    setError(null);
    
    try {
      const toolDetail = await aiManagerApi.getToolDetail(tool.toolId);
      setCurrentToolData(tool);
      setToolSchema(toolDetail.schema);
      setDependencies(toolDetail.dependencies);
      setExecutionHistory(toolDetail.executionHistory);
    } catch (err) {
      console.error('Failed to load tool detail:', err);
      setError('Tool 상세 정보를 불러오는데 실패했습니다.');
      // Fallback to basic tool data
      setCurrentToolData(tool);
      setToolSchema(null);
      setDependencies([]);
      setExecutionHistory([]);
    } finally {
      setLoading(false);
    }
  };

  if (!tool) {
    return null;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Mock data for demonstration
  const mockToolSchema = {
    name: tool.toolName,
    version: '1.0.0',
    author: 'System Admin',
    created: '2024-01-01',
    inputSchema: {
      type: 'object',
      properties: {
        recipientEmail: {
          type: 'string',
          description: '받는 사람 이메일 주소',
          format: 'email'
        },
        subject: {
          type: 'string',
          description: '이메일 제목'
        },
        content: {
          type: 'string',
          description: '이메일 내용'
        },
        attachments: {
          type: 'array',
          description: '첨부파일 목록',
          items: {
            type: 'string'
          }
        }
      },
      required: ['recipientEmail', 'subject', 'content']
    },
    outputSchema: {
      type: 'object',
      properties: {
        success: {
          type: 'boolean',
          description: '전송 성공 여부'
        },
        messageId: {
          type: 'string',
          description: '메시지 ID'
        },
        timestamp: {
          type: 'string',
          description: '전송 시간'
        }
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <ToolIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Tool 상세 정보</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Tool 상세 정보를 불러오는 중...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
        {/* 헤더 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
            <ToolIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {currentToolData?.toolName || tool.toolName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            {currentToolData?.description || tool.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={currentToolData?.isActive ? '활성' : '비활성'} 
              color={currentToolData?.isActive ? 'success' : 'default'} 
              size="small" 
            />
            <Chip 
              label={`카테고리: ${currentToolData?.category || 'utility'}`} 
              color="primary" 
              size="small" 
            />
            <Chip 
              label={`타임아웃 ${currentToolData?.timeoutSeconds || tool.timeoutSeconds}초`} 
              color="warning" 
              size="small" 
            />
            <Chip 
              label={`성공률 ${currentToolData?.successRate || tool.successRate}%`} 
              color="info" 
              size="small" 
            />
            <Chip 
              label={`평균 ${currentToolData?.avgExecutionTime || tool.avgExecutionTime}ms`} 
              color="secondary" 
              size="small" 
            />
          </Box>
        </Box>
          </>
        )}

        {!loading && !error && (
          <>
        {/* 탭 메뉴 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="기본 정보" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="스키마" icon={<CodeIcon />} iconPosition="start" />
            <Tab label="실행 이력" icon={<TimelineIcon />} iconPosition="start" />
            <Tab label="종속성" icon={<DescriptionIcon />} iconPosition="start" />
          </Tabs>
        </Box>        {/* 기본 정보 탭 */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Tool 설정
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Tool ID" 
                        secondary={tool.toolId} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="버전" 
                        secondary={mockToolSchema.version} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="작성자" 
                        secondary={mockToolSchema.author} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="생성일" 
                        secondary={mockToolSchema.created} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="타임아웃" 
                        secondary={`${tool.timeoutSeconds}초`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    실행 통계
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="총 실행 횟수" 
                        secondary={`${tool.executionCount}회`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="성공률" 
                        secondary={`${tool.successRate}%`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="평균 실행 시간" 
                        secondary={`${tool.avgExecutionTime}ms`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="마지막 실행" 
                        secondary={new Date(tool.lastExecutedAt).toLocaleString()} 
                      />
                    </ListItem>
                    {tool.comId && (
                      <ListItem>
                        <ListItemText 
                          primary="도장 ID" 
                          secondary={tool.comId} 
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 스키마 탭 */}
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                입력 스키마
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(mockToolSchema.inputSchema, null, 2)}
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                출력 스키마
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(mockToolSchema.outputSchema, null, 2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 실행 이력 탭 */}
        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            최근 실행 이력
          </Typography>
          {executionHistory.map((execution, index) => (
            <Card key={execution.executionId} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    실행 #{execution.executionId}
                  </Typography>
                  <Chip 
                    label={execution.status === 'success' ? '성공' : '실패'}
                    color={execution.status === 'success' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      시작: {execution.startTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      종료: {execution.endTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      소요시간: {execution.duration}ms
                    </Typography>
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    입력 데이터:
                  </Typography>
                  <Paper sx={{ p: 1, bgcolor: 'grey.50', mb: 1 }}>
                    <Typography variant="body2" component="pre">
                      {JSON.stringify(execution.input, null, 2)}
                    </Typography>
                  </Paper>
                </Box>

                {execution.output && (
                  <Box mt={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      출력 데이터:
                    </Typography>
                    <Paper sx={{ p: 1, bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <Typography variant="body2" component="pre">
                        {JSON.stringify(execution.output, null, 2)}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                
                {execution.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {execution.error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
        </TabPanel>

        {/* 종속성 탭 */}
        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom>
            종속성 목록
          </Typography>
          <Grid container spacing={2}>
            {dependencies.map((dep, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">{dep.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      버전: {dep.version}
                    </Typography>
                    <Chip 
                      label={dep.type === 'external' ? '외부' : '내부'}
                      color={dep.type === 'external' ? 'warning' : 'success'}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {dependencies.length === 0 && (
            <Alert severity="info">
              이 Tool에 종속성이 없습니다.
            </Alert>
          )}
        </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ToolDetailModal;
